import { TransactionStatus, TransactionType, Moment } from '~/types'

export type PersonalAccount = {
  generalWallet: {
    availableBalance: string
  } | null
  digitalGoodsWallet: {
    availableBalance: string
  } | null
  annualSpendingLimit: string | null
  remainingAnnualSpending: string | null
  limitationIncreaseAmount: number | null
  limitationIncreaseDate: Moment | null
}

export type TemporaryHoldingBalance = {
  totalBalance: string
}

export type Transaction = {
  amount: {
    prefix: string
    postfix: string
  }
  created_at: string
  transaction_status: TransactionStatus
  transaction_type: TransactionType
  description: {
    data: string
    direction: 'from' | 'to'
    type: TransactionType
  }
}

export type DepositRequest = {
  amount: string
  bank_account: {
    account_no: string
    bank_name: string
    bank_abbrev: string
  }
  created_at: string
  expired_at: string
}

export type DashboardBanner = {
  title: string
  tag: string
  content: string
}

export type PersonalVerificationStatus = 'initial' | 'pending' | 'verified' | 'rejected'

export enum LANDING_PAGE_ACTIONS {
  SET_USER_INFO = 'LANDING_PAGE_ACTIONS.SET_USER_INFO',
  SET_PERSONAL_ACCOUNT = 'LANDING_PAGE_ACTIONS.SET_PERSONAL_ACCOUNT',
  SET_TEMP_HOLDING_BALANCE = 'LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE',
  SET_LATEST_PERSONAL_TRANSACTIONS = 'LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_TRANSACTIONS',
  SET_LATEST_PERSONAL_DEPOSIT_REQUESTS = 'LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_DEPOSIT_REQUESTS',
  SET_PERSONAL_VERIFICATION_STATUS = 'LANDING_PAGE_ACTIONS.SET_PERSONAL_VERIFICATION_STATUS',
  SET_IS_ERC20_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ERC20_ENABLED',
  SET_IS_ZRC2_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ZRC2_ENABLED',
  SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED',
  SET_IS_ZRC2_NOTICE_BANNER_VISIBLE = 'LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE',
  SET_IS_LONGER_PROCESSING_NOTICE_BANNER_VISIBLE = 'LANDING_PAGE_ACTIONS.SET_IS_LONGER_PROCESSING_NOTICE_BANNER_VISIBLE',
  SET_DASHBOARD_BANNERS = 'ROUTE_ACTIONS.SET_DASHBOARD_BANNERS',
}

type SetUserInfo = {
  type: typeof LANDING_PAGE_ACTIONS.SET_USER_INFO
  name: string
  verificationStatus: 'initial' | 'verified' | 'pending' | 'rejected' | null
  kycVerified: boolean
  kycVerifiedDate: Moment | null
}

type SetPersonalAccount = {
  type: typeof LANDING_PAGE_ACTIONS.SET_PERSONAL_ACCOUNT
  personalAccount: PersonalAccount
}

type SetTempHoldingBalance = {
  type: typeof LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE
  temporaryHoldingBalance: TemporaryHoldingBalance | null
}

type SetLatestPersonalTransactions = {
  type: typeof LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_TRANSACTIONS
  latestPersonalTransactions: Transaction[]
}

type SetLatestPersonalDepositRequests = {
  type: typeof LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_DEPOSIT_REQUESTS
  latestPersonalDepositRequests: DepositRequest[]
}

type SetPersonalVerificationStatus = {
  type: typeof LANDING_PAGE_ACTIONS.SET_PERSONAL_VERIFICATION_STATUS
  personalVerificationStatus: PersonalVerificationStatus
  gauthEnabled: boolean
}

type SetIsErc20Enabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ERC20_ENABLED
  isErc20Enabled: boolean
}

type SetIsZrc2Enabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ZRC2_ENABLED
  isZrc2Enabled: boolean
}

type SetIsZrc2NoticeBannerEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE
  isZrc2NoticeBannerVisible: boolean
}

type SetIsLongerProcessingNoticeBannerEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_LONGER_PROCESSING_NOTICE_BANNER_VISIBLE
  isLongerProcessingNoticeBannerVisible: boolean
}

type SetIsDashboardBannerManagementEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED
  isDashboardBannerManagementEnabled: boolean
}

type SetDashboardBanners = {
  type: typeof LANDING_PAGE_ACTIONS.SET_DASHBOARD_BANNERS
  dashboardBanners: DashboardBanner[]
}

export type ActionTypes =
  | SetUserInfo
  | SetPersonalAccount
  | SetTempHoldingBalance
  | SetLatestPersonalTransactions
  | SetLatestPersonalDepositRequests
  | SetPersonalVerificationStatus
  | SetIsErc20Enabled
  | SetIsZrc2Enabled
  | SetIsZrc2NoticeBannerEnabled
  | SetIsLongerProcessingNoticeBannerEnabled
  | SetIsDashboardBannerManagementEnabled
  | SetDashboardBanners
